import {RESULT_PAGE, sanitizeRichTextHtml} from './utils.js'
import {toPlainText} from './pdfText.js'

const NO_CONTACT_AVAILABLE_I18N_KEY = 'mindestlohnrechner.details.contacts.noContactAvailable'
const SALARY_WAIT_FOR_SELECTION_I18N_KEY = 'mindestlohnrechner.details.salary.waitForSelection'

export function formatDateForPdf(value) {
  if (!value || typeof value !== 'string') {
    return '-'
  }
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) {
    return value
  }
  const [, year, month, day] = match
  return `${day}.${month}.${year}`
}

function toLabelValueRows(rows) {
  return rows.map(([label, value]) => ({ label, value: String(value ?? '-') }))
}

function resolveContractName(meta) {
  const language = getLanguage()
  if (language === 'fr' && meta.nameFr) {
    return meta.nameFr
  }
  if (language === 'it' && meta.nameIt) {
    return meta.nameIt
  }
  return meta.nameDe || meta.contractNumber
}

function buildPdfInputSummaryRows(ctx) {
  const rows = []
  if (ctx.lastSearchInput) {
    rows.push([ctx.i18n.translate('mindestlohnrechner.einsatzort.title'), ctx.lastSearchInput.einsatzortLabel || '-'])
    rows.push([ctx.i18n.translate('mindestlohnrechner.beginn.title'), formatDateForPdf(ctx.lastSearchInput.beginn)])
    rows.push([ctx.i18n.translate('mindestlohnrechner.ende.title'), formatDateForPdf(ctx.lastSearchInput.ende)])
    rows.push([ctx.i18n.translate('mindestlohnrechner.beruf.title'), ctx.lastSearchInput.suchbegriff || '-'])
  }
  const meta = ctx.selectedContractMeta
  if (meta) {
    rows.push([ctx.i18n.translate('mindestlohnrechner.details.selectedContractLabel'), resolveContractName(meta)])
    rows.push([
      ctx.i18n.translate('mindestlohnrechner.details.validityLabel'),
      `${formatDateForPdf(meta.aveStart)} - ${formatDateForPdf(meta.aveEnd)}`
    ])
  }
  return toLabelValueRows(rows)
}

function buildPdfEmployeeRows(ctx) {
  const rows = ctx.getVisibleEmployeeFields().map(field => {
    const selected = ctx.currentSelections[String(field.criteriaStructureId)] || ''
    const option = Array.isArray(field.options) ? field.options.find(candidate => candidate.value === selected) : null
    return [field.label, option?.label || '-']
  })
  if (rows.length === 0) {
    return [{
      label: ctx.i18n.translate('mindestlohnrechner.details.noEmployeeFields'),
      value: '-'
    }]
  }
  return toLabelValueRows(rows)
}

function buildPdfSalaryRows(ctx) {
  const detailRow = ctx.getMatchingDetailRow()
  if (!detailRow) {
    return [{
      label: ctx.i18n.translate(SALARY_WAIT_FOR_SELECTION_I18N_KEY),
      value: '-'
    }]
  }
  return toLabelValueRows([
    [ctx.i18n.translate('mindestlohnrechner.details.salary.basic'), ctx.formatCurrency(detailRow.basicSalary)],
    [ctx.i18n.translate('mindestlohnrechner.details.salary.vacationDays'), String(detailRow.vacationDays ?? '-')],
    [ctx.i18n.translate('mindestlohnrechner.details.salary.vacationCompensation'), ctx.formatCurrency(detailRow.vacationCompensation)],
    [ctx.i18n.translate('mindestlohnrechner.details.salary.publicHolidays'), String(detailRow.publicHolidays ?? '-')],
    [ctx.i18n.translate('mindestlohnrechner.details.salary.publicHolidaysCompensation'), ctx.formatCurrency(detailRow.publicHolidaysCompensation)],
    [
      ctx.i18n.translate('mindestlohnrechner.details.salary.has13th'),
      detailRow.has13thMonthSalary
        ? ctx.i18n.translate('mindestlohnrechner.common.yes')
        : ctx.i18n.translate('mindestlohnrechner.common.no')
    ],
    [ctx.i18n.translate('mindestlohnrechner.details.salary.compensation13th'), ctx.formatCurrency(detailRow.compensation13thSalary)]
  ])
}

function buildPdfContactsText(ctx, contacts) {
  if (!Array.isArray(contacts) || contacts.length === 0) {
    return ctx.i18n.translate(NO_CONTACT_AVAILABLE_I18N_KEY)
  }
  return contacts.map(contact => {
    const lines = [
      contact.name || '',
      contact.zusatzname || '',
      contact.strasse || '',
      contact.zusatz || '',
      [contact.plz, contact.ort].filter(Boolean).join(' '),
      contact.tel ? `Tel: ${contact.tel}` : '',
      contact.email ? `E-Mail: ${contact.email}` : ''
    ].filter(Boolean)
    return lines.join('\n')
  }).join('\n\n')
}

function buildPdfContactEntries(ctx) {
  const meta = ctx.selectedContractMeta
  const typ = meta?.typ || ctx.getSelectedTreffer()?.typ
  const entries = []
  if (typ !== 'NAV') {
    const contractText = ctx.contractParitaetContacts.length > 0
      ? buildPdfContactsText(ctx, ctx.contractParitaetContacts)
      : toPlainText(meta?.weitereAuskunfte || '')
    entries.push({
      title: ctx.i18n.translate('mindestlohnrechner.details.contacts.forContractQuestions'),
      text: contractText || ctx.i18n.translate(NO_CONTACT_AVAILABLE_I18N_KEY)
    })
  }
  entries.push({
    title: ctx.i18n.translate('mindestlohnrechner.details.contacts.cantonalAuthorities'),
    text: buildPdfContactsText(ctx, ctx.noMatchContacts)
  })
  return entries
}

function buildPdfHintEntries(ctx, entries) {
  return entries.map(([titleKey, textKey]) => ({
    title: ctx.i18n.translate(titleKey),
    text: toPlainText(ctx.i18n.translate(textKey))
  }))
}

export function buildPdfSections(ctx, options) {
  const {
    detailsHintsTitleKey,
    detailsHintPersonalverleihTitleKey,
    detailsHintPersonalverleihTextKey,
    detailsHintRechtlicherHinweisTitleKey,
    detailsHintRechtlicherHinweisTextKey,
    detailsTitleGavKurzKey
  } = options

  const sections = [
    {
      type: 'CARD_ROWS',
      title: ctx.i18n.translate('mindestlohnrechner.ergebnis.ihreAngaben'),
      rows: buildPdfInputSummaryRows(ctx),
      paragraphs: [],
      entries: []
    }
  ]

  if (ctx.activeResultPage === RESULT_PAGE.MINDESTLOEHNE) {
    sections.push({
      type: 'CARD_ROWS',
      title: ctx.i18n.translate('mindestlohnrechner.details.employeeDataTitle'),
      rows: buildPdfEmployeeRows(ctx),
      paragraphs: [],
      entries: []
    })
    sections.push({
      type: 'TABLE_ROWS',
      title: ctx.i18n.translate('mindestlohnrechner.details.salary.title'),
      rows: buildPdfSalaryRows(ctx),
      paragraphs: [],
      entries: []
    })
    sections.push({
      type: 'ENTRIES',
      title: ctx.i18n.translate(detailsHintsTitleKey),
      rows: [],
      paragraphs: [],
      entries: buildPdfHintEntries(ctx, [
        ['mindestlohnrechner.details.hints.vollzugskostenbeitraege.title', 'mindestlohnrechner.details.hints.vollzugskostenbeitraege.text'],
        ['mindestlohnrechner.details.hints.internationalerLohnvergleich.title', 'mindestlohnrechner.details.hints.internationalerLohnvergleich.text'],
        ['mindestlohnrechner.details.hints.wechselkurse.title', 'mindestlohnrechner.details.hints.wechselkurse.text'],
        ['mindestlohnrechner.details.hints.anerkennung.title', 'mindestlohnrechner.details.hints.anerkennung.text'],
        [detailsHintPersonalverleihTitleKey, detailsHintPersonalverleihTextKey],
        [detailsHintRechtlicherHinweisTitleKey, detailsHintRechtlicherHinweisTextKey]
      ])
    })
  }

  if (ctx.activeResultPage === RESULT_PAGE.GAV_KURZ) {
    const gavKurzEntries = ctx.getGavKurzSummaryGroups().flatMap(group => {
      const groupTitle = ctx.i18n.translate(group.titleKey).replace(/:\s*$/, '')
      return group.items.map(item => ({
        title: `${groupTitle}: ${ctx.i18n.translate(item.titleKey)}`,
        text: toPlainText(item.content || '-'),
        html: sanitizeRichTextHtml(item.content || '')
      }))
    })
    sections.push({
      type: 'ENTRIES',
      title: ctx.i18n.translate(detailsTitleGavKurzKey),
      rows: [],
      paragraphs: [toPlainText(ctx.i18n.translate('mindestlohnrechner.details.gavKurzIntro'))],
      entries: gavKurzEntries.length > 0 ? gavKurzEntries : [{
        title: ctx.i18n.translate(detailsTitleGavKurzKey),
        text: '-'
      }]
    })
    sections.push({
      type: 'ENTRIES',
      title: ctx.i18n.translate(detailsHintsTitleKey),
      rows: [],
      paragraphs: [],
      entries: buildPdfHintEntries(ctx, [
        ['mindestlohnrechner.details.gavKurzHints.original.title', 'mindestlohnrechner.details.gavKurzHints.original.text'],
        [detailsHintPersonalverleihTitleKey, detailsHintPersonalverleihTextKey],
        [detailsHintRechtlicherHinweisTitleKey, detailsHintRechtlicherHinweisTextKey]
      ])
    })
  }

  if (ctx.activeResultPage === RESULT_PAGE.KONTAKTE) {
    sections.push({
      type: 'ENTRIES',
      title: ctx.i18n.translate('mindestlohnrechner.details.title.kontakte'),
      rows: [],
      paragraphs: [],
      entries: buildPdfContactEntries(ctx)
    })
    sections.push({
      type: 'ENTRIES',
      title: ctx.i18n.translate(detailsHintsTitleKey),
      rows: [],
      paragraphs: [],
      entries: buildPdfHintEntries(ctx, [
        [detailsHintPersonalverleihTitleKey, detailsHintPersonalverleihTextKey],
        [detailsHintRechtlicherHinweisTitleKey, detailsHintRechtlicherHinweisTextKey]
      ])
    })
  }

  return sections
}
